import { Button, ConfirmAction, DataTable, EmptyState } from '@skylabs-digital/backoffice-kit';
import type { ColumnDef } from '@skylabs-digital/tables-react';
import { RotateCw } from 'lucide-react';
import { useMemo } from 'react';
import type { FilesPage } from '../../api/files.api';
import type { FistoFile } from '../../schemas/file.schema';

export interface ProcessingFailuresTableProps {
  page: FilesPage | null;
  loading: boolean;
  error: Error | null;
  onRetry: (f: FistoFile) => void;
  onRetryAll: () => void;
  onPage: (page: number) => void;
}

export function ProcessingFailuresTable({
  page,
  loading,
  error,
  onRetry,
  onRetryAll,
  onPage,
}: ProcessingFailuresTableProps) {
  const columns = useMemo<ColumnDef<FistoFile>[]>(
    () => [
      {
        accessorKey: 'path',
        header: 'Path',
        cell: ({ row }) => <code className="font-mono text-xs">{row.original.path}</code>,
      },
      {
        accessorKey: 'processingError',
        header: 'Error',
        cell: ({ row }) => (
          <span className="text-sm text-destructive">{row.original.processingError}</span>
        ),
      },
      {
        id: 'when',
        header: 'Failed at',
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.processingFinishedAt
              ? new Date(row.original.processingFinishedAt).toLocaleString()
              : '—'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Button variant="outline" size="sm" onClick={() => onRetry(row.original)}>
            <RotateCw /> Retry
          </Button>
        ),
      },
    ],
    [onRetry],
  );
  const total = page?.meta.total ?? 0;
  if (!loading && !error && page && total === 0) {
    return (
      <EmptyState
        title="Nothing failed in this window"
        description="Files that fail processing show up here with their error, ready to retry."
      />
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{total} failed</span>
        {total > 0 ? (
          <ConfirmAction
            trigger={
              <Button variant="outline" size="sm">
                Retry all
              </Button>
            }
            title="Retry every failed file on this page?"
            description="Each file goes back to the queue; the originals are never touched."
            confirmLabel={`Retry ${page?.data.length ?? 0} files`}
            onConfirm={onRetryAll}
          />
        ) : null}
      </div>
      <DataTable
        columns={columns}
        data={page?.data ?? []}
        isLoading={loading}
        error={error}
        hidePagination
        aria-label="Failed files"
      />
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={!page?.meta.hasPrev}
          onClick={() => onPage((page?.meta.page ?? 1) - 1)}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={!page?.meta.hasNext}
          onClick={() => onPage((page?.meta.page ?? 1) + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
